import React, { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { Calculator, Info, AlertTriangle, CheckCircle, ShieldAlert } from 'lucide-react';
import { DownloadPdfButton } from '@/components/DownloadPdfButton';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);
const fmtFull = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const LIFETIME_TABLE = {
  70: 29.1, 71: 28.2, 72: 27.4, 73: 26.5, 74: 25.5, 75: 24.6,
  76: 23.7, 77: 22.9, 78: 22.0, 79: 21.1, 80: 20.2, 81: 19.4,
  82: 18.5, 83: 17.7, 84: 16.8, 85: 16.0, 86: 15.2, 87: 14.4,
  88: 13.7, 89: 12.9, 90: 12.2,
};

function rmdAgeFromBirthYear(yr) {
  if (yr <= 1950) return 70;
  if (yr <= 1959) return 73;
  return 75;
}

export const PensionRMDPage = () => {
  const [loading, setLoading] = useState(true);

  // Shared data
  const [salary, setSalary] = useState(85000);
  const [currentAge, setCurrentAge] = useState(35);
  const [birthYear, setBirthYear] = useState(1991);
  const [currentYrsService, setCurrentYrsService] = useState(5);
  const [tradBalance, setTradBalance] = useState(0);
  const [rothBalance, setRothBalance] = useState(0);

  // FERS inputs
  const [high3, setHigh3] = useState(85000);
  const [retireAge, setRetireAge] = useState(62);
  const [yearsService, setYearsService] = useState(33);

  // RMD inputs
  const [stillWorking, setStillWorking] = useState(false);
  const [returnRate, setReturnRate] = useState(6);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [sumRes, balRes] = await Promise.allSettled([
        api.get('/tsp/summary'),
        api.get('/tsp/participant/balance'),
      ]);
      if (sumRes.status === 'fulfilled') {
        const s = sumRes.value.data;
        setSalary(s?.account?.annual_salary || 85000);
        setHigh3(s?.account?.annual_salary || 85000);
        setCurrentAge(s?.participant?.age || 35);
        const yrs = s?.vesting?.years_of_service || 5;
        setCurrentYrsService(yrs);
        setYearsService(Math.round(yrs + Math.max(62 - (s?.participant?.age || 35), 0)));
        setBirthYear(s?.rmd?.birth_year || 1991);
      }
      if (balRes.status === 'fulfilled') {
        const b = balRes.value.data;
        setTradBalance(b.traditional || 0);
        setRothBalance(b.roth || 0);
      }
    } catch {} finally { setLoading(false); }
  };

  // FERS Pension calc
  const pension = useMemo(() => {
    const enhanced = retireAge >= 62 && yearsService >= 20;
    const mult = enhanced ? 0.011 : 0.01;
    const annual = high3 * mult * yearsService;
    return {
      enhanced, multiplier: mult, multPct: `${(mult * 100).toFixed(1)}%`,
      annual, monthly: annual / 12,
      condition: enhanced ? 'Age >= 62 AND >= 20 years of service' : 'Standard (age < 62 or < 20 years)',
      formula: `${(mult * 100).toFixed(1)}% × $${high3.toLocaleString()} × ${yearsService} yrs = $${annual.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
    };
  }, [high3, yearsService, retireAge]);

  // RMD calc
  const rmd = useMemo(() => {
    const rmdAge = rmdAgeFromBirthYear(birthYear);
    const yrsUntil = Math.max(rmdAge - currentAge, 0);
    const r = returnRate / 100;
    // Project Traditional balance to RMD age
    let projTrad = tradBalance;
    if (r > 0 && yrsUntil > 0) {
      const gf = (1 + r) ** yrsUntil;
      projTrad = tradBalance * gf;
    } else if (yrsUntil > 0) {
      projTrad = tradBalance;
    }
    const divisor = LIFETIME_TABLE[rmdAge] || 24.6;
    const annualRmd = projTrad / divisor;

    // 5-year schedule
    const schedule = [];
    let bal = projTrad;
    for (let i = 0; i < 5; i++) {
      const a = rmdAge + i;
      const d = LIFETIME_TABLE[a] || Math.max(24.6 - i, 10);
      const yr = bal / d;
      schedule.push({ age: a, divisor: d, balance: bal, rmd: yr });
      bal = (bal - yr) * (1 + r);
    }

    return { rmdAge, yrsUntil, projTrad, divisor, annualRmd, monthlyRmd: annualRmd / 12, schedule };
  }, [birthYear, currentAge, tradBalance, returnRate]);

  if (loading) return <div className="panel p-6 animate-pulse"><div className="h-6 bg-gray-100 rounded w-48 mb-4" /><div className="h-60 bg-gray-50 rounded" /></div>;

  const inputClass = "w-full h-10 px-3 bg-white border border-[#D1D5DB] rounded text-sm text-[#1B2A4A] font-mono focus:outline-none focus:border-[#1B2A4A] focus:ring-1 focus:ring-[#1B2A4A]/20";
  const labelClass = "block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1";

  return (
    <div className="space-y-6" data-testid="pension-rmd-page">

      {/* ══════════ SECTION A: FERS PENSION ══════════ */}
      <div data-testid="fers-pension-section">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-lg font-semibold text-[#1B2A4A]" style={{ fontFamily: 'IBM Plex Sans' }}>FERS Pension Calculator — 5 U.S.C. § 8415</h1>
          <DownloadPdfButton
            path="/exports/fers-pension.pdf"
            params={{ high3, years_service: yearsService, target_retirement_age: retireAge }}
            filename="UniFed_FERSPension.pdf"
            label="Download Pension PDF"
            testId="download-pension-btn"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Inputs */}
          <div className="panel p-5">
            <span className="overline mb-4 block">Pension Inputs</span>
            <div className="space-y-3">
              <div><label className={labelClass}>High-3 Average Salary</label><input type="number" value={Math.round(high3)} onChange={e => setHigh3(parseFloat(e.target.value) || 0)} className={inputClass} data-testid="pension-high3" /></div>
              <div><label className={labelClass}>Years of Service at Retirement</label><input type="number" value={yearsService} onChange={e => setYearsService(parseInt(e.target.value) || 0)} className={inputClass} data-testid="pension-years" /></div>
              <div><label className={labelClass}>Target Retirement Age</label><input type="number" min={50} max={75} value={retireAge} onChange={e => setRetireAge(parseInt(e.target.value) || 62)} className={inputClass} data-testid="pension-retire-age" /></div>
              <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded px-4 py-2.5 text-xs">
                <div className="flex justify-between"><span className="text-[#6B7280]">Current Age</span><span className="font-mono font-medium">{currentAge}</span></div>
                <div className="flex justify-between mt-1"><span className="text-[#6B7280]">Current Years of Service</span><span className="font-mono font-medium">{currentYrsService.toFixed(1)}</span></div>
              </div>
            </div>
          </div>

          {/* Result */}
          <div className="panel p-5" data-testid="pension-result">
            <span className="overline mb-3 block">Pension Estimate</span>

            {/* Multiplier badge */}
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded text-xs font-medium mb-4 ${pension.enhanced ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-gray-50 border border-[#E2E5E9] text-[#6B7280]'}`} data-testid="multiplier-badge">
              {pension.enhanced ? <CheckCircle size={13} /> : <Info size={13} />}
              <span>{pension.multPct} multiplier — {pension.condition}</span>
            </div>

            {/* Formula breakdown */}
            <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-4 mb-4" data-testid="pension-formula-display">
              <span className="text-[10px] text-[#9CA3AF] block mb-1">Calculation</span>
              <p className="font-mono text-sm text-[#1B2A4A]" data-testid="pension-formula-text">
                <span className="font-semibold">{pension.multPct}</span> × <span className="font-semibold">{fmtFull(high3)}</span> × <span className="font-semibold">{yearsService} yrs</span> = <span className="font-bold text-[#1B2A4A]">{fmtFull(pension.annual)}</span>
              </p>
            </div>

            {/* Annual / Monthly */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#1B2A4A] rounded p-4 text-white">
                <span className="text-[10px] text-[#8B95A5] uppercase tracking-wider">Annual Pension</span>
                <div className="font-mono text-2xl font-bold mt-1" data-testid="pension-annual">{fmt(pension.annual)}</div>
              </div>
              <div className="bg-[#2D5F8A] rounded p-4 text-white">
                <span className="text-[10px] text-[#B8C5D8] uppercase tracking-wider">Monthly Pension</span>
                <div className="font-mono text-2xl font-bold mt-1" data-testid="pension-monthly">{fmt(pension.monthly)}</div>
              </div>
            </div>

            {pension.enhanced && (
              <div className="mt-3 flex items-center gap-2 text-xs text-emerald-600">
                <CheckCircle size={12} />
                <span>1.1% bonus multiplier applied (age {retireAge} ≥ 62 and {yearsService} yrs ≥ 20)</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ══════════ SECTION B: RMD CALCULATOR ══════════ */}
      <div data-testid="rmd-calculator-section">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-lg font-semibold text-[#1B2A4A]" style={{ fontFamily: 'IBM Plex Sans' }}>RMD Calculator — SECURE 2.0</h1>
          <DownloadPdfButton
            path="/exports/rmd-calculator.pdf"
            params={{ still_working_past_rmd: stillWorking, annual_return_pct: returnRate }}
            filename="UniFed_RMDCalculator.pdf"
            label="Download RMD PDF"
            testId="download-rmd-btn"
          />
        </div>

        {/* Roth exemption */}
        <div className="panel p-4 bg-emerald-50 border-emerald-200 mb-4" data-testid="roth-exemption-notice">
          <div className="flex items-start gap-2">
            <CheckCircle size={14} className="text-emerald-600 mt-0.5 shrink-0" />
            <p className="text-xs text-emerald-700">
              Your Roth balance (<span className="font-mono font-semibold">{fmtFull(rothBalance)}</span>) is <span className="font-semibold">not subject to RMD</span> during your lifetime. Only your Traditional balance is included in the RMD calculation. (SECURE 2.0, effective 2024)
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* RMD Info */}
          <div className="panel p-5">
            <span className="overline mb-4 block">RMD Determination</span>
            <div className="space-y-3 text-xs">
              <div className="flex justify-between py-2 border-b border-[#F0F1F3]"><span className="text-[#6B7280]">Birth Year</span><span className="font-mono font-medium text-[#1B2A4A]">{birthYear}</span></div>
              <div className="flex justify-between py-2 border-b border-[#F0F1F3]"><span className="text-[#6B7280]">Current Age</span><span className="font-mono font-medium text-[#1B2A4A]">{currentAge}</span></div>
              <div className="flex justify-between py-2 border-b border-[#F0F1F3]">
                <span className="text-[#6B7280]">RMD Age</span>
                <span className="font-mono font-semibold text-[#1B2A4A]" data-testid="rmd-age-display">{rmd.rmdAge}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-[#F0F1F3]"><span className="text-[#6B7280]">Years Until RMD</span><span className="font-mono font-medium text-[#1B2A4A]">{rmd.yrsUntil}</span></div>
              <div className="flex justify-between py-2 border-b border-[#F0F1F3]"><span className="text-[#6B7280]">Current Traditional Balance</span><span className="font-mono font-medium text-[#1B2A4A]">{fmtFull(tradBalance)}</span></div>
              <div className="flex justify-between py-2 border-b border-[#F0F1F3]"><span className="text-[#6B7280]">Projected Traditional at RMD Age</span><span className="font-mono font-semibold text-[#1B2A4A]" data-testid="projected-trad-at-rmd">{fmt(rmd.projTrad)}</span></div>
              <div className="flex justify-between py-2"><span className="text-[#6B7280]">IRS Divisor (Age {rmd.rmdAge})</span><span className="font-mono font-medium text-[#1B2A4A]">{rmd.divisor}</span></div>
            </div>

            {/* Still-working toggle */}
            <div className="mt-4 bg-[#F8F9FA] border border-[#E2E5E9] rounded p-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={stillWorking} onChange={e => setStillWorking(e.target.checked)}
                  className="h-4 w-4 text-[#1B2A4A] border-gray-300 rounded focus:ring-[#1B2A4A]" data-testid="still-working-toggle" />
                <span className="text-xs text-[#374151]">I plan to work past age {rmd.rmdAge}</span>
              </label>
              {stillWorking && (
                <p className="mt-2 text-[10px] text-blue-600 ml-7">RMD delayed until year of separation from federal service. Still-working exception applies.</p>
              )}
            </div>

            {/* Return rate */}
            <div className="mt-4">
              <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Assumed Annual Return %</label>
              <div className="flex items-center gap-2"><input type="range" min={0} max={10} step={0.5} value={returnRate} onChange={e => setReturnRate(parseFloat(e.target.value))} className="flex-1" data-testid="rmd-return-slider" /><span className="font-mono text-sm font-semibold text-[#1B2A4A] w-10 text-right">{returnRate}%</span></div>
            </div>
          </div>

          {/* RMD Result */}
          <div className="space-y-4">
            <div className="panel p-5" data-testid="rmd-result">
              <span className="overline mb-3 block">First Year RMD</span>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#1B2A4A] rounded p-4 text-white">
                  <span className="text-[10px] text-[#8B95A5] uppercase tracking-wider">Annual RMD</span>
                  <div className="font-mono text-2xl font-bold mt-1" data-testid="annual-rmd">{fmt(rmd.annualRmd)}</div>
                </div>
                <div className="bg-[#2D5F8A] rounded p-4 text-white">
                  <span className="text-[10px] text-[#B8C5D8] uppercase tracking-wider">Monthly RMD</span>
                  <div className="font-mono text-2xl font-bold mt-1" data-testid="monthly-rmd">{fmt(rmd.monthlyRmd)}</div>
                </div>
              </div>
              <p className="mt-3 text-[10px] text-[#9CA3AF]">
                {fmtFull(rmd.projTrad)} ÷ {rmd.divisor} (Uniform Lifetime Table, age {rmd.rmdAge}) = {fmtFull(rmd.annualRmd)}/year
              </p>
            </div>

            {/* 5-Year Schedule */}
            <div className="panel p-5" data-testid="rmd-schedule">
              <span className="overline mb-3 block">5-Year RMD Schedule</span>
              <table className="w-full text-xs">
                <thead><tr className="border-b border-[#E2E5E9] text-[10px] text-[#6B7280] uppercase tracking-wider">
                  <th className="py-2 text-left">Age</th><th className="py-2 text-right">Divisor</th><th className="py-2 text-right">Balance</th><th className="py-2 text-right">RMD</th>
                </tr></thead>
                <tbody>{rmd.schedule.map((row, i) => (
                  <tr key={i} className="border-b border-[#F0F1F3]">
                    <td className="py-2 font-mono font-medium text-[#1B2A4A]">{row.age}</td>
                    <td className="py-2 text-right font-mono text-[#6B7280]">{row.divisor.toFixed(1)}</td>
                    <td className="py-2 text-right font-mono text-[#1B2A4A]">{fmt(row.balance)}</td>
                    <td className="py-2 text-right font-mono font-medium text-[#1B2A4A]">{fmtFull(row.rmd)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>

            {/* Penalty disclosure */}
            <div className="bg-red-50 border border-red-200 rounded px-4 py-3" data-testid="rmd-penalty-disclosure">
              <div className="flex items-start gap-2">
                <ShieldAlert size={14} className="text-red-600 mt-0.5 shrink-0" />
                <p className="text-xs text-red-700 leading-relaxed">
                  Missed RMD incurs a <span className="font-semibold">25% excise tax</span> on the amount not distributed. This is reducible to <span className="font-semibold">10%</span> if the missed distribution is corrected within 2 years. (SECURE 2.0; 26 U.S.C. § 4974)
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="text-[10px] text-[#9CA3AF] text-center leading-relaxed">
        FERS Pension: 5 U.S.C. § 8415 · RMD: SECURE 2.0; 26 U.S.C. § 4974 · IRS Uniform Lifetime Table · Projections are estimates only
      </div>
    </div>
  );
};
